import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type WaitRoomProps = {
  accessCode: string;
  apiBaseUrl: string;
  onSessionFound: (sessionId: string) => void;
  onUnauthorized: () => void;
};

type WaitRoomStatus = "checking" | "waiting" | "error";

export function WaitRoom({
  accessCode,
  apiBaseUrl,
  onSessionFound,
  onUnauthorized,
}: WaitRoomProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_status, setStatus] = useState<WaitRoomStatus>("checking");
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(
    () => {
      if (!accessCode) return;

      let cancelled = false;
      let ws: WebSocket | null = null;

      const openWebSocket = () => {
        if (cancelled) return;

        setStatus("waiting");

        try {
          // Reuse the same base host but switch http -> ws, https -> wss
          const wsBase = apiBaseUrl.replace(/^http/, "ws");
          const wsUrl = `${wsBase}/ws/session-notifications?access-code=${encodeURIComponent(
            accessCode
          )}`;

          console.log(`Opening WS URL ${wsUrl}`);
          ws = new WebSocket(wsUrl);

          ws.onmessage = (event: MessageEvent) => {
            if (cancelled) return;

            let sessionId: string | null = null;

            try {
              const parsed = JSON.parse(event.data);
              if (typeof parsed === "string") {
                sessionId = parsed;
              } else if (parsed?.sessionId) {
                sessionId = String(parsed.sessionId);
              } else if (parsed?.session?.sessionId) {
                sessionId = String(parsed.session.sessionId);
              }
            } catch {
              // If it's not JSON, assume it's just a raw sessionId string
              if (typeof event.data === "string") {
                sessionId = event.data;
              }
            }

            if (sessionId) {
              onSessionFound(sessionId);
              ws?.close();
            }
          };

          ws.onerror = (evt) => {
            console.error("WaitRoom WebSocket error", evt);
            if (!cancelled) {
              setStatus("error");
              setError(t("errors.genericErrorExtended"));
            }
          };

          ws.onclose = () => {
            ws = null;
          };
        } catch (err) {
          console.error("WaitRoom failed to open WebSocket", err);
          if (!cancelled) {
            setStatus("error");
            setError(t("errors.genericErrorExtended"));
          }
        }
      };

      const checkOngoingSessions = async () => {
        setStatus("checking");
        setError(null);

        try {
          const resp = await fetch(`${apiBaseUrl}/ongoing-sessions`, {
            headers: {
              "x-connect-access-code": accessCode,
            },
          });

          if (!resp.ok) {
            console.error(
              "WaitRoom: failed to fetch ongoing sessions",
              resp.status
            );
            if (resp.status === 401) {
              onUnauthorized();
            } else {
              // Fall back to WS
              openWebSocket();
            }
            return;
          }

          const data = await resp.json().catch(() => null as unknown);

          // Expect something like { sessions: [{ sessionId: string }, ...] }
          const sessions =
            (data && (data.sessions ?? data.sessionList ?? data.items)) || [];
          const first =
            Array.isArray(sessions) && sessions.length > 0 ? sessions[0] : null;

          const sessionId =
            first?.sessionId ??
            first?.id ??
            (typeof first === "string" ? first : null);

          if (sessionId && !cancelled) {
            onSessionFound(String(sessionId));
            return;
          }

          if (!cancelled) {
            openWebSocket();
          }
        } catch (err) {
          console.error("WaitRoom: error while checking ongoing sessions", err);
          if (!cancelled) {
            setError(t("errors.genericErrorExtended"));
            openWebSocket();
          }
        }
      };

      checkOngoingSessions();

      return () => {
        cancelled = true;
        if (ws && ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiBaseUrl, onSessionFound, setStatus, accessCode]
  );

  return (
    <div id="wait-room" className="card text-center mb-6 mt-3 wait-room">
      <p className="text-xl mb-2">{t("waitRoom.heading")}</p>
      <p className="text-white font-thin text-sm">{t("waitRoom.text")}</p>
      {error && <p className="mt-2 text-sm error">{error}</p>}
    </div>
  );
}
